import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from '@/routing/Link'
import { useRouter, useSearchParams } from '@/routing/navigation'
import { Plus, Search, Store, Users, X } from 'lucide-react'
import { AudienceCarousel } from './components/AudienceCarousel'
import { CollapsibleAudienceGrid } from './components/CollapsibleAudienceGrid'
import { PageContent } from '@/components/layout/PageContent'
import { PageLayout } from '@/components/layout/PageLayout'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useProfile } from '@/lib/hooks'
import { useAudiencesData } from './hooks/useAudiencesData'
import type { CompanyProfileData } from './components/ProviderSection'
import IndustrySection from './components/IndustrySection'
import type { IndustryData } from './components/IndustrySection'
import ViewAllPanel from './components/ViewAllPanel'
import CinematicAudienceCard from './components/CinematicAudienceCard'
import AudienceActionMenu from './components/AudienceActionMenu'
import { AudienceDetailModal } from './components/AudienceDetailModal'
import type { AudienceSummary } from '@/lib/api'
import { cn, labelFromBrandDomain } from '@/lib/utils'
import {
  MARKETPLACE_ORG_DOMAIN_PREFIX,
  MARKETPLACE_ORGANIZATION_UNKNOWN_TOKEN,
} from '@/lib/marketplace-audience-org-keys'

import { ComposeModal } from '@/pages/ConversationsPage/ComposeModal/ComposeModal'
import { LobbyEmptyState } from '@/components/shared/LobbyEmptyState'
import { audienceTierDisplayName } from '@/lib/billing/display-names'
import { getIndustryDisplayName, getIndustryIcon } from '@/lib/constants/industry-categories'
import { CreditIcon } from '@/components/icons/CreditIcon'
import { resolveAudienceCanonicalCoverUrl, resolveAudienceTitleFont } from './utils/audience-presentation'
import { isExternalCollaboratorAudience } from './utils/audience-share-routing'
import { FALLBACK_DISPLAY_FONT_CLASS } from './utils/display-fonts'
import { getDisplayFontConfig, responsiveFontVars } from './utils/font-config'
import { AUDIENCE_CARD_GRID_CLASS } from './hooks/useAudienceGridColumns'
import { AudienceFilterModal, FilterTriggerButton } from './components/AudienceFilterPanel'
import { FilterEmptyState } from './components/FilterEmptyState'
import { COUNTRIES } from '@/lib/constants/demographics'
import { filterAudiences } from '@/lib/utils/audience-filter-predicate'
import useSWR from 'swr'
import { taxonomyApi } from '@/lib/api'
import { useIndustryTokenMeta } from './hooks/useIndustryTokenMeta'
import { useTranslation } from 'react-i18next'
import { buildCategoryFilterChips } from './utils/category-filter-chips'
import {
  clearStoredCategoryTokenNames,
  mergeStoredCategoryTokenNames,
  pruneStoredCategoryTokenNames,
} from './utils/category-filter-name-storage'

export default function AudiencesPage() {
  const { t, i18n } = useTranslation('app')
  const [searchQuery, setSearchQuery] = useState('')
  const [viewAllProvider, setViewAllProvider] = useState<CompanyProfileData | null>(null)
  const [viewAllIndustry, setViewAllIndustry] = useState<IndustryData | null>(null)
  const router = useRouter()
  const searchParams = useSearchParams()
  const selectedAudienceId = searchParams.get('audience')

  // Initialize filter state from URL params
  const categoriesFromUrl = searchParams.get('categories')?.split(',').filter(Boolean) || []
  const countriesFromUrl = searchParams.get('countries')?.split(',').filter(Boolean) || []
  const rawType = searchParams.get('type')
  const typeFromUrl = rawType === 'B2B' || rawType === 'B2C' ? rawType : null
  const hasUrlFilters = !!(categoriesFromUrl.length > 0 || countriesFromUrl.length > 0 || typeFromUrl)

  const [filterExpanded, setFilterExpanded] = useState(hasUrlFilters)
  const [selectedCategoryTokens, setSelectedCategoryTokens] = useState<Array<string>>(categoriesFromUrl)
  const [categoryTokenNames, setCategoryTokenNames] = useState<Record<string, string>>(() =>
    pruneStoredCategoryTokenNames(categoriesFromUrl),
  )
  const [selectedCountryCodes, setSelectedCountryCodes] = useState<Array<string>>(countriesFromUrl)
  const [audienceTypeFilter, setAudienceTypeFilter] = useState<'B2B' | 'B2C' | null>(typeFromUrl)

  // Sync filter state → URL (skip the initial render to avoid a navigation on mount)
  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    const params = new URLSearchParams(searchParams.toString())
    if (selectedCategoryTokens.length) params.set('categories', selectedCategoryTokens.join(','))
    else params.delete('categories')
    if (selectedCountryCodes.length) params.set('countries', selectedCountryCodes.join(','))
    else params.delete('countries')
    if (audienceTypeFilter) params.set('type', audienceTypeFilter)
    else params.delete('type')

    const newQs = params.toString()
    const currentQs = searchParams.toString()
    if (newQs !== currentQs) {
      router.push(newQs ? `?${newQs}` : '/audiences', { scroll: false })
    }
  }, [selectedCategoryTokens, selectedCountryCodes, audienceTypeFilter, router, searchParams])

  const selectAudience = useCallback((id: string) => {
    if (window.innerWidth < 768) {
      router.push(`/audiences/${id}`)
      return
    }
    const params = new URLSearchParams(searchParams.toString())
    params.set('audience', id)
    router.push(`?${params.toString()}`, { scroll: false })
  }, [router, searchParams])

  const clearAudience = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString())
    params.delete('audience')
    const qs = params.toString()
    router.push(qs ? `?${qs}` : '/audiences', { scroll: false })
  }, [router, searchParams])

  const [composeOpen, setComposeOpen] = useState(false)
  const [composeAudienceId, setComposeAudienceId] = useState<string | null>(null)
  const [composeAudienceSnapshot, setComposeAudienceSnapshot] = useState<AudienceSummary | null>(null)

  const openComposeForAudience = useCallback((payload: { audienceId: string; audience?: AudienceSummary | null }) => {
    setComposeAudienceId(payload.audienceId)
    setComposeAudienceSnapshot(payload.audience ?? null)
    setComposeInitialTwin(null)
    setComposeOpen(true)
  }, [])

  const handleComposeOpenChange = useCallback((open: boolean) => {
    setComposeOpen(open)
    if (!open) {
      setComposeAudienceId(null)
      setComposeAudienceSnapshot(null)
      setComposeInitialTwin(null)
    }
  }, [])

  const [composeInitialTwin, setComposeInitialTwin] = useState<{ personaId: string; populationId: string; personaName?: string; imageUrl?: string | null } | null>(null)

  const openComposeForTwin = useCallback((twin: { personaId: string; populationId: string; personaName?: string; imageUrl?: string }) => {
    setComposeInitialTwin(twin)
    setComposeOpen(true)
  }, [])

  const { data: profile, mutate: refreshProfile } = useProfile()

  // Profile (incl. featureFlags.createAudience) lives in AuthProvider and is not
  // refetched on client navigation. Revalidate here so the create-audience CTAs
  // appear after upgrades without a full page reload.
  useEffect(() => {
    void refreshProfile()
  }, [refreshProfile])

  const {
    audiences,
    audiencesLoading,
    deletingAudienceId,
    togglingAudienceGlobalId,
    isPlatformAdmin,
    canPublishMarketplace,
    handleDeleteAudience,
    removeAudienceFromList,
    refreshAudiences,
    handleToggleAudienceGlobal,
    handleBookmarkToggle,
    formatRelativeTime,
    publicAudiencesByIndustry,
    companyProfiles,
  } = useAudiencesData(profile)

  const canPublish = isPlatformAdmin || canPublishMarketplace

  // "My audiences" title is now always literal — no company-name fallback
  const orgAudiencesTitle = t('audiences.title')

  // Bookmarked audiences sorted by bookmarkedAt desc (all visible audience types)
  const bookmarkedAudiences = useMemo(() => {
    return audiences
      .filter((a: AudienceSummary) => a.isBookmarked)
      .sort((a, b) => {
        const ta = a.bookmarkedAt ? new Date(a.bookmarkedAt).getTime() : 0
        const tb = b.bookmarkedAt ? new Date(b.bookmarkedAt).getTime() : 0
        return tb - ta
      })
  }, [audiences])

  const bookmarkedIds = useMemo(
    () => new Set(bookmarkedAudiences.map((a: AudienceSummary) => a.id)),
    [bookmarkedAudiences],
  )

  const privateAudiences = useMemo(() => {
    return audiences
      .filter((a: AudienceSummary) => {
        if (a.sharingMode === 'marketplace') return false
        return a.canEdit !== false
      })
      .filter((a: AudienceSummary) => !bookmarkedIds.has(a.id))
      .sort((a, b) => new Date(b.updatedAt ?? b.createdAt).getTime() - new Date(a.updatedAt ?? a.createdAt).getTime())
  }, [audiences, bookmarkedIds])

  const sharedAudiences = useMemo(() => {
    return audiences
      .filter((a: AudienceSummary) => a.sharingMode !== 'marketplace' && !a.canEdit && a.canEdit !== undefined)
      .filter((a: AudienceSummary) => !bookmarkedIds.has(a.id))
      .sort((a, b) => new Date(b.updatedAt ?? b.createdAt).getTime() - new Date(a.updatedAt ?? a.createdAt).getTime())
  }, [audiences, bookmarkedIds])

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null
    const q = searchQuery.toLowerCase()
    return audiences.filter((a: AudienceSummary) => {
      const haystack = [
        a.name,
        a.aiDescription,
        a.description,
        a.audienceType,
        a.publisherDomain,
        a.organizationName,
        a.industryLabel?.name,
        a.industryLabel?.fullPath,
        ...(a.contentLabels?.map((l) => l.name) || a.aiTags || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [audiences, searchQuery])

  const publicAudiences = useMemo(
    () => audiences.filter((a: AudienceSummary) => a.sharingMode === 'marketplace'),
    [audiences],
  )

  // -- Industry tier-1 nodes for ancestry matching --
  const { data: tier1Data } = useSWR(
    'taxonomy-industry-tier1',
    () => taxonomyApi.getNodes('industry', { tier: 1 }),
    { revalidateOnFocus: false, dedupingInterval: 60_000 },
  )
  // Resolve UUID tokens to names (tier-2/3 need taxonomy lookup beyond tier-1 browse)
  const tokenMeta = useIndustryTokenMeta(
    audiences,
    tier1Data?.nodes,
    selectedCategoryTokens,
    categoryTokenNames,
  )

  const categoryFilterChips = useMemo(
    () => buildCategoryFilterChips(selectedCategoryTokens, tokenMeta),
    [selectedCategoryTokens, tokenMeta],
  )

  // -- Filtered view --
  const hasActiveFilters = !!(selectedCategoryTokens.length > 0 || selectedCountryCodes.length > 0 || audienceTypeFilter)
  const filterActiveCount =
    selectedCategoryTokens.length + selectedCountryCodes.length + (audienceTypeFilter ? 1 : 0)

  const filteredAudiences = useMemo(() => {
    return filterAudiences(audiences, {
      categoryTokens: selectedCategoryTokens,
      countryCodes: selectedCountryCodes,
      audienceType: audienceTypeFilter,
    })
  }, [audiences, selectedCategoryTokens, selectedCountryCodes, audienceTypeFilter])

  const ownAudiences = useMemo<Array<AudienceSummary>>(() => {
    const seen = new Set<string>()
    const result: Array<AudienceSummary> = []
    for (const a of [...bookmarkedAudiences, ...privateAudiences, ...sharedAudiences]) {
      if (!seen.has(a.id)) {
        seen.add(a.id)
        result.push(a)
      }
    }
    return result
  }, [bookmarkedAudiences, privateAudiences, sharedAudiences])

  const filteredOwnAudiences = useMemo<Array<AudienceSummary>>(() => {
    if (!hasActiveFilters) return []
    return filterAudiences(ownAudiences, {
      categoryTokens: selectedCategoryTokens,
      countryCodes: selectedCountryCodes,
      audienceType: audienceTypeFilter,
    }) ?? []
  }, [ownAudiences, hasActiveFilters, selectedCategoryTokens, selectedCountryCodes, audienceTypeFilter])

  const filteredMarketplaceAudiences = useMemo<Array<AudienceSummary>>(() => {
    if (!filteredAudiences) return []
    const ownIds = new Set(ownAudiences.map((a) => a.id))
    return filteredAudiences.filter((a) => !ownIds.has(a.id))
  }, [filteredAudiences, ownAudiences])

  function handleClearAllFilters() {
    setSelectedCategoryTokens([])
    setSelectedCountryCodes([])
    setAudienceTypeFilter(null)
    setCategoryTokenNames({})
    clearStoredCategoryTokenNames()
  }

  const viewAllAudiences = useMemo(() => {
    if (viewAllIndustry) {
      const match = publicAudiencesByIndustry.find((g) => g.industry.token === viewAllIndustry.token)
      return match?.audiences ?? []
    }
    if (!viewAllProvider) return []
    if (viewAllProvider.domain.startsWith(MARKETPLACE_ORG_DOMAIN_PREFIX)) {
      const token = viewAllProvider.domain.slice(MARKETPLACE_ORG_DOMAIN_PREFIX.length)
      if (token === MARKETPLACE_ORGANIZATION_UNKNOWN_TOKEN) {
        return publicAudiences.filter((a: AudienceSummary) => !a.organizationToken?.trim())
      }
      return publicAudiences.filter((a: AudienceSummary) => a.organizationToken === token)
    }
    return publicAudiences.filter(
      (a: AudienceSummary) => a.publisherDomain === viewAllProvider.domain,
    )
  }, [publicAudiences, publicAudiencesByIndustry, viewAllProvider, viewAllIndustry])

  const viewerOrgToken = profile?.organizationToken

  function isExternalCollaborator(a: { canEdit?: boolean; organizationToken?: string }): boolean {
    return isExternalCollaboratorAudience({
      audienceOrganizationToken: a.organizationToken,
      viewerOrganizationToken: viewerOrgToken,
    })
  }

  function getSharedByOrgName(a: AudienceSummary): string | null {
    if (!isExternalCollaborator(a)) return null
    return a.organizationName?.trim() || null
  }

  const renderActionMenu = (audience: { id: string; name: string; status?: string; sharingMode?: string; canEdit?: boolean; organizationToken?: string }) => (
    <AudienceActionMenu
      audience={{
        id: audience.id,
        name: audience.name,
        status: audience.status,
        canEdit: audience.canEdit,
      }}
      deletingAudienceId={deletingAudienceId}
      handleDeleteAudience={handleDeleteAudience}
      onLeft={() => removeAudienceFromList(audience.id)}
    />
  )

  const heroAudiences = useMemo(() => {
    return audiences.filter((a: AudienceSummary) => a.status !== 'error' && a.status !== 'processing' && resolveAudienceCanonicalCoverUrl(a))
  }, [audiences])

  const [heroIndex, setHeroIndex] = useState(0)
  const [heroCtaExpanded, setHeroCtaExpanded] = useState(false)

  useEffect(() => {
    if (heroAudiences.length <= 1) return
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroAudiences.length)
      setHeroCtaExpanded(false)
    }, 5000)
    return () => clearInterval(interval)
  }, [heroAudiences.length])

  const heroAudience = heroAudiences[heroIndex % Math.max(heroAudiences.length, 1)] || null

  const heroProvider = useMemo(() => {
    if (!heroAudience || heroAudience.sharingMode !== 'marketplace') return null
    const nonempty = (s: string | null | undefined): string | null => {
      const t = s?.trim()
      return t ? t : null
    }
    const domain = heroAudience.publisherDomain?.trim()
    if (domain) {
      const profile = companyProfiles.find((p) => p.domain === domain)
      const name =
        nonempty(heroAudience.organizationName)
        ?? nonempty(profile?.name)
        ?? labelFromBrandDomain(domain)
        ?? domain
      const logoUrl =
        nonempty(heroAudience.logoUrl)
        ?? nonempty(profile?.logoUrl ?? null)
        ?? null
      return { name, logoUrl }
    }
    const orgLabel = nonempty(heroAudience.organizationName)
    if (orgLabel) {
      return { name: orgLabel, logoUrl: nonempty(heroAudience.logoUrl) ?? null }
    }
    return null
  }, [heroAudience, companyProfiles])

  const heroIndustryTag = useMemo(() => {
    if (!heroAudience) return null
    const label = heroAudience.industryLabel
    if (label) {
      return {
        key: label.token,
        name: getIndustryDisplayName(label.name),
        icon: getIndustryIcon(label.fullPath.split(' | ')[0]),
      }
    }
    const first = heroAudience.contentLabels?.[0]
    if (first) {
      return {
        key: first.token,
        name: getIndustryDisplayName(first.name),
        icon: getIndustryIcon(first.name),
      }
    }
    return null
  }, [heroAudience])

  const heroTierLabel = useMemo(() => {
    if (!heroAudience) return null
    return heroAudience.tier ? audienceTierDisplayName(heroAudience.tier) : 'Standard'
  }, [heroAudience])

  const heroDigitalTwinCount = useMemo(() => {
    if (!heroAudience) return null
    return heroAudience.personaCount ?? heroAudience.rowCount ?? null
  }, [heroAudience])

  return (
    <PageLayout variant="wide" className="px-4 md:px-8 pt-0 pb-8">
      {/* Hero Banner skeleton */}
      {audiencesLoading && (
        <div className="relative w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] h-[360px] overflow-hidden -mx-4 md:-mx-8">
          <Skeleton className="absolute inset-0 rounded-none" />
          {/* Bottom content skeleton */}
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-col gap-3 min-w-0 flex-1">
              <Skeleton className="h-10 w-72 bg-muted/40 rounded" />
              <Skeleton className="h-5 w-[420px] max-w-full bg-muted/30 rounded" />
              <div className="flex gap-2 flex-wrap">
                <Skeleton className="h-7 w-16 rounded-full bg-muted/30" />
                <Skeleton className="h-7 w-24 rounded-full bg-muted/30" />
                <Skeleton className="h-7 w-20 rounded-full bg-muted/30" />
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Skeleton className="h-10 w-32 rounded-lg bg-muted/30" />
              <Skeleton className="h-10 w-36 rounded-lg bg-muted/30" />
            </div>
          </div>
        </div>
      )}

      {/* Hero Banner — full-width, auto-rotating between highlighted audiences */}
      {heroAudiences.length > 0 && !audiencesLoading && (
        <div
          role="button"
          tabIndex={0}
          data-testid="audiences-hero-banner"
          onClick={() => heroAudience && selectAudience(heroAudience.id)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); heroAudience && selectAudience(heroAudience.id) } }}
          className="relative block w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] h-[360px] overflow-hidden group text-left -mx-4 md:-mx-8 shadow-home-card cursor-pointer"
        >
          {/* Crossfade background images */}
          {heroAudiences.map((audience, idx) => (
            <img
              key={audience.id}
              src={resolveAudienceCanonicalCoverUrl(audience)!}
              alt=""
              loading={idx === heroIndex % heroAudiences.length ? 'eager' : 'lazy'}
              className={cn(
                'absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out',
                '[object-position:center_50%] md:[object-position:center_calc(50%+140px)]',
                idx === heroIndex % heroAudiences.length ? 'opacity-100' : 'opacity-0',
              )}
              aria-hidden
            />
          ))}
          <div
            className="absolute bottom-0 left-0 right-0 h-1/2"
            style={{
              background: 'var(--audience-hero-image-overlay)',
            }}
          />

          {/* Brand chip — top-left */}
          {heroProvider && (
            <div className="absolute top-4 left-4 z-10">
              <div className="flex items-center gap-1.5 bg-black/45 backdrop-blur-[12px] px-2.5 py-1 rounded-full">
                {heroProvider.logoUrl && (
                  <img src={heroProvider.logoUrl} alt="" className="w-5 h-5 rounded-[5px] object-cover" />
                )}
                <span className="text-xs font-medium text-white/90">
                  {heroProvider.name}
                </span>
              </div>
            </div>
          )}

          {/* Content — bottom, fades with active audience */}
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8 z-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-10">
            <div className="min-w-0 flex-1 flex flex-col gap-2">
              {heroAudience && (() => {
                const resolvedTitleFont = resolveAudienceTitleFont(heroAudience)
                const titleFontFamily = resolvedTitleFont
                  ? `'${resolvedTitleFont}', sans-serif`
                  : undefined
                const fallbackFontClass = !titleFontFamily
                  ? FALLBACK_DISPLAY_FONT_CLASS
                  : undefined
                const fontConfig = getDisplayFontConfig(resolvedTitleFont)
                return (
                  <h2
                    key={heroAudience.id}
                    className={cn(
                      'text-[length:var(--hero-fs-sm)] md:text-[length:var(--hero-fs)]',
                      'text-white tracking-tight animate-in fade-in duration-500 line-clamp-2 pt-[4px] pb-[4px]',
                      fallbackFontClass,
                    )}
                    style={responsiveFontVars(fontConfig.hero, 'hero', titleFontFamily)}
                  >
                    {heroAudience.name}
                  </h2>
                )
              })()}
              {(() => {
                const heroTagline =
                  heroAudience?.stageMetadata?.tagline?.trim() ||
                  heroAudience?.narrativeCard?.header?.trim() ||
                  heroAudience?.aiDescription?.trim() ||
                  null
                if (!heroTagline) return null
                return (
                  <p key={`tagline-${heroAudience?.id}`} className="text-[20px] font-medium text-white mt-2 line-clamp-2 animate-in fade-in duration-500">
                    {heroTagline}
                  </p>
                )
              })()}
              {/* Tag pills — below tagline */}
              {heroAudience && (
                <div className="flex flex-wrap items-center gap-2 mt-2 animate-in fade-in duration-500">
                  {heroAudience.audienceType && (
                    <span className="inline-flex items-center rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 text-sm font-medium text-white/90">
                      {heroAudience.audienceType}
                    </span>
                  )}
                  {heroIndustryTag && (() => {
                    const Icon = heroIndustryTag.icon
                    return (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 text-sm font-medium text-white/90">
                        <Icon className="h-3.5 w-3.5" />
                        {heroIndustryTag.name}
                      </span>
                    )
                  })()}
                  {heroTierLabel && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 text-sm font-medium text-white/90">
                      <CreditIcon className="h-3.5 w-3.5" />
                      {heroTierLabel}
                    </span>
                  )}
                  {heroDigitalTwinCount != null && (
                    <span className="text-sm text-white/50 font-normal">
                      {t('audiences.digitalTwins', {
                    count: heroDigitalTwinCount,
                    formatted: heroDigitalTwinCount.toLocaleString(i18n.language),
                  })}
                    </span>
                  )}
                </div>
              )}
            </div>

            {heroAudience && (
              <div
                className="shrink-0 flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant="outline"
                  className="h-10 rounded-lg px-4 text-sm font-medium border-white/70 text-white bg-transparent hover:bg-white/10 hover:text-white"
                  onClick={() => selectAudience(heroAudience.id)}
                  data-testid="hero-view-audience-button"
                >
                  {t('audiences.viewAudience')}
                </Button>
                <Button
                  variant="cta"
                  className="h-10 rounded-lg px-4 text-sm font-medium"
                  asChild
                  data-testid="hero-create-study-button"
                >
                  <Link href={`/tests/new?audience=${heroAudience.id}`}>
                    {t('audiences.startStudy')}
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <AudienceFilterModal
        open={filterExpanded}
        onOpenChange={setFilterExpanded}
        state={{ categoryTokens: selectedCategoryTokens, countryCodes: selectedCountryCodes, audienceType: audienceTypeFilter }}
        categoryTokenNames={categoryTokenNames}
        onApply={(s, namesByToken) => {
          setSelectedCategoryTokens(s.categoryTokens)
          setSelectedCountryCodes(s.countryCodes)
          setAudienceTypeFilter(s.audienceType)
          if (namesByToken && Object.keys(namesByToken).length > 0) {
            setCategoryTokenNames((prev) =>
              mergeStoredCategoryTokenNames({ ...prev, ...namesByToken }),
            )
          }
        }}
        audiences={audiences}
      />

      {/* Active filter chips */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-0 py-2" data-testid="active-filter-chips">
          <div className="flex flex-wrap items-center gap-2">
          {categoryFilterChips.length >= 4 ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="gap-1 pr-1">
                    <button
                      type="button"
                      onClick={() => setFilterExpanded(true)}
                      data-testid="filter-chip-edit-categories"
                      className="hover:text-foreground transition-colors"
                    >
                      {t('audiences.industryCount', { count: categoryFilterChips.length })}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCategoryTokens([])
                        setCategoryTokenNames({})
                        pruneStoredCategoryTokenNames([])
                      }}
                      data-testid="filter-chip-remove-all-categories"
                      className="ml-0.5 rounded-full p-0.5 hover:bg-foreground/10 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-xs">{categoryFilterChips.map((c) => c.name).join(', ')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            categoryFilterChips.map((chip) => (
              <Badge
                key={chip.token}
                variant="secondary"
                className="gap-1 pr-1"
              >
                {chip.name}
                <button
                  type="button"
                  onClick={() => {
                    const nextTokens = selectedCategoryTokens.filter((t) => t !== chip.token)
                    setSelectedCategoryTokens(nextTokens)
                    setCategoryTokenNames((prev) => {
                      const next = { ...prev }
                      delete next[chip.token]
                      return next
                    })
                    pruneStoredCategoryTokenNames(nextTokens)
                  }}
                  data-testid={`filter-chip-remove-category-${chip.token}`}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-foreground/10 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))
          )}
          {selectedCountryCodes.length >= 4 ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="gap-1 pr-1">
                    <button
                      type="button"
                      onClick={() => setFilterExpanded(true)}
                      data-testid="filter-chip-edit-countries"
                      className="hover:text-foreground transition-colors"
                    >
                      {t('audiences.countriesCount', { count: selectedCountryCodes.length })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedCountryCodes([])}
                      data-testid="filter-chip-remove-all-countries"
                      className="ml-0.5 rounded-full p-0.5 hover:bg-foreground/10 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-xs">
                    {selectedCountryCodes
                      .map((code) => COUNTRIES.find((c) => c.code === code)?.name || code)
                      .join(', ')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            selectedCountryCodes.map((code) => (
              <Badge key={code} variant="secondary" className="gap-1 pr-1">
                {COUNTRIES.find((c) => c.code === code)?.name || code}
                <button
                  type="button"
                  onClick={() => setSelectedCountryCodes((prev) => prev.filter((c) => c !== code))}
                  data-testid={`filter-chip-remove-country-${code}`}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-foreground/10 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))
          )}
          {audienceTypeFilter && (
            <Badge variant="secondary" className="gap-1 pr-1">
              {audienceTypeFilter}
              <button
                type="button"
                onClick={() => setAudienceTypeFilter(null)}
                data-testid="filter-chip-remove-type"
                className="ml-0.5 rounded-full p-0.5 hover:bg-foreground/10 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          <button
            type="button"
            onClick={handleClearAllFilters}
            data-testid="filter-chips-clear-all"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Clear all
          </button>
          </div>
          <FilterTriggerButton
            activeCount={filterActiveCount}
            onClick={() => setFilterExpanded(true)}
          />
        </div>
      )}

      <PageContent className="gap-12 pt-2">
        {filteredAudiences !== null ? (
          /* Filtered view — own audiences section + marketplace grid */
          filteredOwnAudiences.length === 0 && filteredMarketplaceAudiences.length === 0 ? (
            <FilterEmptyState
              selectedCategoryPath={undefined}
              selectedCountryCodes={selectedCountryCodes}
              audienceTypeFilter={audienceTypeFilter}
              allAudiences={audiences}
              onAudienceClick={selectAudience}
              formatRelativeTime={formatRelativeTime}
              onBookmarkToggle={handleBookmarkToggle}
            />
          ) : (
            <div className="space-y-10">
              {filteredOwnAudiences.length > 0 && (
                <div>
                  <Heading level={2} className="mb-4">{orgAudiencesTitle}</Heading>
                  <div className={cn('grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5')}>
                    {filteredOwnAudiences.map((audience: AudienceSummary) => (
                      <CinematicAudienceCard
                        key={audience.id}
                        audience={audience}
                        provider={null}
                        accessType={audience.sharingMode === 'marketplace' ? 'shared' : 'private'}
                        formatRelativeTime={formatRelativeTime}
                        onClick={() => selectAudience(audience.id)}
                        actionMenu={
                          (audience.canEdit || isExternalCollaborator(audience))
                            ? renderActionMenu(audience)
                            : undefined
                        }
                        onBookmarkToggle={handleBookmarkToggle}
                        sharedByOrgName={getSharedByOrgName(audience)}
                      />
                    ))}
                  </div>
                </div>
              )}
              {filteredMarketplaceAudiences.length > 0 && (
                <div>
                  <Heading level={2} className="mb-4">
                    {t('audiences.matchingFilters', { count: filteredMarketplaceAudiences.length })}
                  </Heading>
                  <div className={cn('grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5')}>
                    {filteredMarketplaceAudiences.map((audience: AudienceSummary) => {
                      const companyMatch =
                        audience.sharingMode === 'marketplace' && audience.publisherDomain
                          ? companyProfiles.find((p) => p.domain === audience.publisherDomain)
                          : undefined
                      const filterCardProvider = companyMatch
                        ? { name: companyMatch.name, logoUrl: companyMatch.logoUrl }
                        : null
                      return (
                        <CinematicAudienceCard
                          key={audience.id}
                          audience={audience}
                          provider={filterCardProvider}
                          accessType={audience.sharingMode === 'marketplace' ? 'shared' : 'private'}
                          formatRelativeTime={formatRelativeTime}
                          onClick={() => selectAudience(audience.id)}
                          actionMenu={
                            (audience.sharingMode !== 'marketplace' || audience.canEdit)
                              ? renderActionMenu(audience)
                              : undefined
                          }
                          onBookmarkToggle={handleBookmarkToggle}
                        />
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )
        ) : searchResults ? (
          <div>
            <Heading level={2} className="mb-4">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}{' '}
              <span className="text-muted-foreground">
                for &ldquo;{searchQuery.trim()}&rdquo;
              </span>
            </Heading>
            {searchResults.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {searchResults.map((audience: AudienceSummary) => {
                  const companyMatch =
                    audience.sharingMode === 'marketplace' && audience.publisherDomain
                      ? companyProfiles.find((p) => p.domain === audience.publisherDomain)
                      : undefined
                  const searchCardProvider = companyMatch
                    ? { name: companyMatch.name, logoUrl: companyMatch.logoUrl }
                    : null
                  return (
                  <CinematicAudienceCard
                    key={audience.id}
                    audience={audience}
                    provider={searchCardProvider}
                    accessType={audience.sharingMode === 'marketplace' ? 'shared' : 'private'}
                    formatRelativeTime={formatRelativeTime}
                    onClick={() => selectAudience(audience.id)}
                    actionMenu={
                      (audience.sharingMode !== 'marketplace' || audience.canEdit)
                        ? renderActionMenu(audience)
                        : undefined
                    }
                    onBookmarkToggle={handleBookmarkToggle}
                  />
                  )
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center py-20 text-center">
                <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                  <Search className="h-7 w-7 text-muted-foreground" />
                </div>
                <Heading level={3} className="mb-1.5">
                  {t('audiences.noneFound')}
                </Heading>
                <Text size="sm" color="muted">
                  {t('audiences.noneFoundHint')}
                </Text>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Private audiences section */}
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                {audiencesLoading ? (
                  <Skeleton className="h-10 w-48" />
                ) : (
                  <h2 className="font-display text-2xl md:text-4xl text-foreground" style={{ letterSpacing: '-0.02em' }}>
                    {orgAudiencesTitle}
                  </h2>
                )}
                <div className="flex items-center gap-2">
                  <FilterTriggerButton
                    activeCount={filterActiveCount}
                    onClick={() => setFilterExpanded(true)}
                  />
                  <Button
                    asChild
                    variant="cta"
                    className="hidden md:flex h-10 rounded-lg px-4 text-base font-medium"
                    data-testid="audiences-create-button"
                  >
                    <Link href="/audiences/new">
                      <Plus className="mr-2 h-4 w-4" />
                      {t('audiences.create')}
                    </Link>
                  </Button>
                </div>
              </div>
              {audiencesLoading ? (
                <>
                  {/* Mobile loading skeleton */}
                  <AudienceCarousel className="md:hidden">
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} className="h-[300px] w-[280px] shrink-0 rounded-[12px]" />
                    ))}
                  </AudienceCarousel>
                  {/* Desktop loading skeleton */}
                  <div className={cn('hidden md:grid', AUDIENCE_CARD_GRID_CLASS)}>
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} className="aspect-square rounded-[12px]" />
                    ))}
                  </div>
                </>
              ) : (() => {
                const allCards = [
                  ...bookmarkedAudiences.map((a: AudienceSummary) => ({ audience: a, accessType: a.sharingMode === 'marketplace' ? 'shared' as const : 'private' as const })),
                  ...privateAudiences.map((a: AudienceSummary) => ({ audience: a, accessType: 'private' as const })),
                  ...sharedAudiences.map((a: AudienceSummary) => ({ audience: a, accessType: 'shared' as const })),
                ]
                if (allCards.length === 0) {
                  return (
                    <div className="grid grid-cols-1">
                      <LobbyEmptyState
                        title={t('audiences.emptyTitle')}
                        description={t('audiences.emptyBody')}
                      >
                        <Button asChild variant="secondary" className="rounded-[6px] h-10 px-4 text-base">
                          <Link href="/audiences/new">
                            <Plus className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} />
                            {t('audiences.createFirst')}
                          </Link>
                        </Button>
                      </LobbyEmptyState>
                    </div>
                  )
                }
                const desktopCards = allCards.map(({ audience, accessType }) => (
                  <CinematicAudienceCard
                    key={audience.id}
                    audience={audience}
                    provider={null}
                    accessType={accessType}
                    formatRelativeTime={formatRelativeTime}
                    onClick={() => selectAudience(audience.id)}
                    actionMenu={audience.canEdit || isExternalCollaborator(audience) ? renderActionMenu(audience) : undefined}
                    onBookmarkToggle={handleBookmarkToggle}
                    sharedByOrgName={getSharedByOrgName(audience)}
                  />
                ))
                return (
                  <>
                    {/* Mobile: swipeable carousel */}
                    <div className="md:hidden flex flex-col gap-3">
                      <AudienceCarousel>
                        {allCards.map(({ audience, accessType }) => (
                          <CinematicAudienceCard
                            key={audience.id}
                            audience={audience}
                            provider={null}
                            accessType={accessType}
                            formatRelativeTime={formatRelativeTime}
                            onClick={() => selectAudience(audience.id)}
                            actionMenu={audience.canEdit || isExternalCollaborator(audience) ? renderActionMenu(audience) : undefined}
                            onBookmarkToggle={handleBookmarkToggle}
                            sharedByOrgName={getSharedByOrgName(audience)}
                          />
                        ))}
                      </AudienceCarousel>
                      <Button
                        asChild
                        variant="cta"
                        className="w-full h-10 rounded-lg px-4 text-base font-medium justify-center"
                        data-testid="audiences-create-button-mobile"
                      >
                        <Link href="/audiences/new">
                          <Plus className="mr-2 h-4 w-4" />
                          {t('audiences.create')}
                        </Link>
                      </Button>
                    </div>

                    {/* Desktop: responsive grid + load more/less */}
                    <div className="hidden md:block">
                      <CollapsibleAudienceGrid cards={desktopCards} />
                    </div>
                  </>
                )
              })()}
            </div>

            {/* Marketplace section — grouped by industry */}
            {(audiencesLoading || publicAudiencesByIndustry.length > 0) && (
              <div className="space-y-6">
                {/* Marketplace section header */}
                <div className="flex items-center gap-2">
                  {audiencesLoading ? (
                    <Skeleton className="h-10 w-64" />
                  ) : (
                    <>
                      <div className="flex items-center justify-center w-[22px] h-[22px] rounded-full bg-[hsl(var(--audience-badge-bg))] shrink-0">
                        <Store className="h-3 w-3 text-[hsl(var(--audience-badge-fg))]" />
                      </div>
                      <h2 className="font-display text-2xl md:text-4xl text-foreground" style={{ letterSpacing: '-0.02em' }}>
                        {t('audiences.marketplace')}
                      </h2>
                    </>
                  )}
                </div>

                {audiencesLoading ? (
                  <div className="space-y-10">
                    {[1, 2, 3].map((i) => (
                      <div key={i}>
                        <Skeleton className="h-7 w-40 mb-4" />
                        <div className="overflow-x-auto -mx-4 md:mx-0">
                          <div className="flex gap-4 px-4 md:px-0">
                            {[1, 2, 3].map((j) => (
                              <Skeleton key={j} className="w-[280px] sm:w-[340px] shrink-0 aspect-square rounded-[12px]" />
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  publicAudiencesByIndustry.map(({ industry, audiences: industryAudiences }, sectionIndex) => (
                    <div
                      key={industry.token}
                      className={cn(sectionIndex > 0 && 'pt-6 mt-6')}
                    >
                      <IndustrySection
                        industry={industry}
                        audiences={industryAudiences}
                        formatRelativeTime={formatRelativeTime}
                        onViewAll={setViewAllIndustry}
                        onCardClick={(id) => selectAudience(id)}
                        renderActionMenu={(audience) =>
                          audience.canEdit ? renderActionMenu(audience) : undefined
                        }
                        onBookmarkToggle={handleBookmarkToggle}
                      />
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

      </PageContent>

      {/* Audience detail modal */}
      {selectedAudienceId && (
        <AudienceDetailModal
          audienceId={selectedAudienceId}
          coverImageUrl={resolveAudienceCanonicalCoverUrl(audiences.find(a => a.id === selectedAudienceId))}
          initialCanEdit={audiences.find(a => a.id === selectedAudienceId)?.canEdit}
          canPublishMarketplace={canPublish}
          onAudienceChanged={refreshAudiences}
          onClose={clearAudience}
          onOpenChat={openComposeForAudience}
          onOpenChatWithTwin={openComposeForTwin}
        />
      )}

      <ComposeModal
        open={composeOpen}
        onOpenChange={handleComposeOpenChange}
        initialTwin={composeInitialTwin}
        initialAudienceId={composeAudienceId}
        initialAudienceSnapshot={composeAudienceSnapshot}
      />

      {/* View All slide-over panel */}
      <ViewAllPanel
        provider={viewAllIndustry
          ? { domain: viewAllIndustry.token, name: viewAllIndustry.name, audienceCount: viewAllAudiences.length }
          : viewAllProvider
        }
        audiences={viewAllAudiences}
        open={!!viewAllProvider || !!viewAllIndustry}
        onClose={() => { setViewAllProvider(null); setViewAllIndustry(null) }}
        formatRelativeTime={formatRelativeTime}
        onCardClick={(id) => selectAudience(id)}
        renderActionMenu={(audience) =>
          audience.canEdit ? renderActionMenu(audience) : undefined
        }
        onBookmarkToggle={handleBookmarkToggle}
      />
    </PageLayout>
  )
}
